import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingDown, TrendingUp, Calendar, RefreshCw, ChevronDown } from 'lucide-react';
import api from '../utils/api';



const RateHistoryChart = ({ user }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(90);
  const [selectedRates, setSelectedRates] = useState(['best_fixed_5y', 'boc_rate']);
  const [showDropdown, setShowDropdown] = useState(false);

  const periods = [
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
    { label: '6 Months', value: 180 },
    { label: '1 Year', value: 365 }
  ];

  const rateOptions = [
    { key: 'boc_rate', label: 'BoC Rate', color: '#ef4444' },
    { key: 'prime_rate', label: 'Prime Rate', color: '#f97316' },
    { key: 'best_fixed_3y', label: '3Y Fixed', color: '#3b82f6' },
    { key: 'best_fixed_5y', label: '5Y Fixed', color: '#10b981' },
    { key: 'best_variable', label: 'Variable', color: '#8b5cf6' }
  ];

  useEffect(() => {
    fetchRateHistory();
  }, [selectedPeriod]);

  const fetchRateHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/rates/history?days=${selectedPeriod}`);
      setHistoryData(response.data.history || []);
    } catch (err) {
      console.error('Error fetching rate history:', err);
      setError('Failed to load rate history');
    } finally {
      setLoading(false);
    }
  };

  const toggleRate = (rateKey) => {
    setSelectedRates(prev => {
      if (prev.includes(rateKey)) {
        return prev.filter(r => r !== rateKey);
      }
      return [...prev, rateKey];
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const formatTooltipDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const calculateTrend = (data, rateKey) => {
    if (!data || data.length < 2) return { direction: 'stable', change: 0 };
    
    const recent = data.slice(-7);
    if (recent.length < 2) return { direction: 'stable', change: 0 };
    
    const firstRate = recent[0][rateKey];
    const lastRate = recent[recent.length - 1][rateKey];
    const change = lastRate - firstRate;
    
    return {
      direction: change < -0.05 ? 'declining' : change > 0.05 ? 'rising' : 'stable',
      change: change.toFixed(2)
    };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-sm font-medium text-slate-300 mb-2">{formatTooltipDate(label)}</p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-xs text-slate-400">{entry.name}</span>
              </div>
              <span className="text-sm font-mono font-medium" style={{ color: entry.color }}>
                {entry.value}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const trend5Y = calculateTrend(historyData, 'best_fixed_5y');

  return (
    <div data-testid="rate-history-chart" className="bg-card border border-border rounded-xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Rate History
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Track mortgage rate trends over time
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-lg text-sm hover:bg-muted transition-colors"
            >
              {periods.find(p => p.value === selectedPeriod)?.label}
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 mt-1 w-32 bg-card border border-border rounded-lg shadow-xl z-10">
                {periods.map(period => (
                  <button
                    key={period.value}
                    onClick={() => {
                      setSelectedPeriod(period.value);
                      setShowDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      selectedPeriod === period.value ? 'bg-primary/10 text-primary' : ''
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={fetchRateHistory}
            disabled={loading}
            className="p-2 bg-muted/50 border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Rate Type Toggles */}
      <div className="flex flex-wrap gap-2 mb-6">
        {rateOptions.map(option => (
          <button
            key={option.key}
            onClick={() => toggleRate(option.key)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              selectedRates.includes(option.key)
                ? 'bg-opacity-20 border-2'
                : 'bg-muted/30 border border-border opacity-50 hover:opacity-75'
            }`}
            style={{
              backgroundColor: selectedRates.includes(option.key) ? `${option.color}20` : undefined,
              borderColor: selectedRates.includes(option.key) ? option.color : undefined,
              color: selectedRates.includes(option.key) ? option.color : undefined
            }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: option.color }}
            ></span>
            {option.label}
          </button>
        ))}
      </div>

      {/* Trend Summary */}
      <div className="flex items-center gap-4 mb-6 p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-2">
          {trend5Y.direction === 'declining' ? (
            <TrendingDown className="h-5 w-5 text-green-400" />
          ) : trend5Y.direction === 'rising' ? (
            <TrendingUp className="h-5 w-5 text-red-400" />
          ) : (
            <div className="h-5 w-5 flex items-center justify-center">
              <div className="w-4 h-0.5 bg-yellow-400"></div>
            </div>
          )}
          <span className="text-sm">
            5Y Fixed rates are{' '}
            <span className={`font-medium ${
              trend5Y.direction === 'declining' ? 'text-green-400' : 
              trend5Y.direction === 'rising' ? 'text-red-400' : 'text-yellow-400'
            }`}>
              {trend5Y.direction}
            </span>
            {trend5Y.change !== '0.00' && (
              <span className="text-muted-foreground"> ({trend5Y.change > 0 ? '+' : ''}{trend5Y.change}% this week)</span>
            )}
          </span>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-80 flex items-center justify-center">
          <div className="flex items-center gap-3 text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading rate history...</span>
          </div>
        </div>
      ) : error ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 mb-2">{error}</p>
            <button
              onClick={fetchRateHistory}
              className="text-sm text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      ) : historyData.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <p className="text-muted-foreground">No historical data available yet</p>
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={historyData}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.5} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#334155' }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={['dataMin - 0.5', 'dataMax + 0.5']}
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#334155' }}
                tickFormatter={(value) => `${value}%`}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: '12px' }}
              />
              
              {rateOptions.map(option => (
                selectedRates.includes(option.key) && (
                  <Line
                    key={option.key}
                    type="monotone"
                    dataKey={option.key}
                    name={option.label}
                    stroke={option.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2 }}
                  />
                )
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Data Info */}
      {historyData.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <span>{historyData.length} data points</span>
          <span>
            {historyData[0]?.date} to {historyData[historyData.length - 1]?.date}
          </span>
        </div>
      )}
    </div>
  );
};

export default RateHistoryChart;
